import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';

function normalizeSpots(raw) {
  const arr = Array.isArray(raw) ? raw : [];
  const out = [];
  for (let i = 0; i < 4; i += 1) {
    out.push({
      name: (arr[i]?.name && String(arr[i].name).trim()) || '',
      image: arr[i]?.image || null
    });
  }
  return out;
}

/**
 * Grade estilo bento (4 cards) — cidades ou bairros com contagem, imagem e "Ver mais".
 */
export default function HomeLocationSpotsSection({
  mode = 'city',
  /** 'properties' | 'condominiums' */
  entityType = 'properties',
  spots: spotsProp,
  countMap = {},
  primaryColor
}) {
  const spots = normalizeSpots(spotsProp).filter((s) => s.name);
  if (spots.length === 0) return null;

  const listBase = entityType === 'condominiums' ? '/condominios' : '/propriedades';

  const toHref = (name) =>
    mode === 'neighborhood'
      ? `${listBase}?neighborhood=${encodeURIComponent(name)}`
      : `${listBase}?location=${encodeURIComponent(name)}`;

  const countFor = (name) => countMap[name] ?? 0;

  const sectionTitle =
    entityType === 'condominiums'
      ? mode === 'neighborhood'
        ? 'Condomínios por bairros'
        : 'Condomínios por cidades'
      : mode === 'neighborhood'
        ? 'Imóveis por bairros'
        : 'Imóveis por cidades';

  const badgeUnit = entityType === 'condominiums' ? 'CONDOMÍNIO' : 'IMÓVEL';
  const badgeUnitPlural = entityType === 'condominiums' ? 'CONDOMÍNIOS' : 'IMÓVEIS';

  const btnStyle = primaryColor
    ? {
        borderColor: primaryColor,
        color: '#fff',
        backgroundColor: `${primaryColor}e6`
      }
    : undefined;

  const cells = spots.slice(0, 4);
  /** Mobile: 1 coluna, altura fixa igual para os 4; sm+: bento com min-heights. Grid sem min-height no mobile (evita “esmagar” linhas). */
  const gridClass =
    cells.length === 4
      ? 'grid grid-cols-1 sm:grid-cols-3 sm:grid-rows-2 gap-4 md:gap-5 sm:min-h-[720px] lg:min-h-[800px]'
      : 'grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5';

  const cellClass = (index) => {
    const base = 'relative block w-full rounded-2xl overflow-hidden group shadow-lg';
    if (cells.length !== 4) {
      return `${base} h-[260px] min-h-[260px] sm:h-auto sm:min-h-[280px] md:min-h-[320px]`;
    }
    const map = [
      'max-sm:h-[280px] max-sm:min-h-[280px] max-sm:shrink-0 sm:col-span-2 sm:min-h-[300px] md:min-h-[340px] lg:min-h-[380px]',
      'max-sm:h-[280px] max-sm:min-h-[280px] max-sm:shrink-0 sm:col-span-1 sm:min-h-[300px] md:min-h-[340px] lg:min-h-[380px]',
      'max-sm:h-[280px] max-sm:min-h-[280px] max-sm:shrink-0 sm:col-span-1 sm:min-h-[300px] md:min-h-[340px] lg:min-h-[380px]',
      'max-sm:h-[280px] max-sm:min-h-[280px] max-sm:shrink-0 sm:col-span-2 sm:min-h-[300px] md:min-h-[340px] lg:min-h-[380px]'
    ];
    return `${base} ${map[index] || ''}`;
  };

  return (
    <section className="py-16 bg-white overflow-hidden border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-10 md:mb-12" data-aos="fade-up">
          <h2 className="text-3xl font-montserrat font-semibold mb-3 text-gray-900">{sectionTitle}</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Explore oportunidades nas regiões que mais combinam com você
          </p>
        </div>

        <div className={gridClass}>
          {cells.map((spot, index) => {
            const n = countFor(spot.name);
            const badgeText = `${n} ${n === 1 ? badgeUnit : badgeUnitPlural}`;

            return (
              <Link
                key={`${spot.name}-${index}`}
                to={toHref(spot.name)}
                className={cellClass(index)}
                data-aos="fade-up"
                data-aos-delay={index * 80}
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{
                    backgroundImage: spot.image
                      ? `url(${spot.image})`
                      : undefined,
                    backgroundColor: spot.image ? undefined : '#1e293b'
                  }}
                />
                {!spot.image && (
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900" />
                )}

                <div className="absolute top-3 left-3 md:top-4 md:left-4 z-10">
                  <span className="inline-block px-2.5 py-1 md:px-3 md:py-1.5 text-[10px] md:text-xs font-bold tracking-wide uppercase rounded-full text-white border border-white/15 bg-black/10 backdrop-blur-md shadow-md [box-shadow:inset_0_1px_0_rgba(255,255,255,0.06)]">
                    {badgeText}
                  </span>
                </div>

                {/* Metade inferior: degradê visível, um pouco mais fraco que o overlay cheio original */}
                <div
                  className="pointer-events-none absolute inset-x-0 bottom-0 top-1/2 z-[1] bg-gradient-to-t from-black/60 via-black/28 to-transparent"
                  aria-hidden
                />

                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 z-10 flex flex-col items-start gap-2">
                  <h3 className="text-xl md:text-2xl font-montserrat font-semibold text-white drop-shadow-md line-clamp-2">
                    {spot.name}
                  </h3>
                  <span
                    className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg text-[13px] font-semibold leading-none border border-white/85 text-white bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors"
                    style={btnStyle}
                  >
                    Ver mais
                    <FaArrowRight className="w-3 h-3 shrink-0 opacity-90" aria-hidden />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export { normalizeSpots };
